import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useReservationModal } from "../contexts/ReservationModalContext";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Events", to: "/events" },
  { label: "Reservations", to: "/reservations" },
  { label: "VIP", to: "/vip" },
  { label: "Menu", to: "/menu" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Careers", to: "/careers" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { open: openReservation } = useReservationModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isHero = location.pathname === "/";
  const lightMode = isHero && !scrolled && !open;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled || open || !isHero ? "bg-white shadow-sm border-b border-black/8" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <Logo variant={lightMode ? "light" : "dark"} size="md" showTagline={false} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            const baseColor = lightMode ? "text-white/85 hover:text-white" : "text-black/75 hover:text-black";
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-[11px] tracking-luxe uppercase font-medium transition-colors duration-300 ${active ? "text-gold" : baseColor}`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid="nav-reserve-cta"
            onClick={() => openReservation()}
            className="btn-luxe btn-luxe-gold !py-3 !px-7"
          >
            Reserve
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${lightMode ? "text-white" : "text-ink"}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-black/5">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={`text-sm tracking-luxe-tight uppercase font-medium ${location.pathname === l.to ? "text-gold" : "text-black/85"}`}
              >
                {l.label}
              </Link>
            ))}
            <button
              data-testid="mobile-nav-reserve"
              onClick={() => openReservation()}
              className="btn-luxe btn-luxe-gold mt-4"
            >
              Reserve Table
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
